import { Box, Button, Stack, Typography } from "@mui/material";
import type { ReactNode } from "react";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Modal from "./Modal";
import ComingSoonBanner from "./ComingSoonBanner";
import ComingSoonFeatures, { type ComingSoonFeature } from "./ComingSoonFeatures";
import { HTTPClient } from "../../HTTPClient";
import { useAuth } from "../../providers/AuthProvider";

export type { ComingSoonFeature };

interface ComingSoonModalProps {
  open: boolean;
  onClose: () => void;
  interest: string;
  // Banner props
  bannerTitle: string;
  bannerTag1: string;
  bannerTag2: string;
  bannerGradient?: string;
  bannerIconSrc?: string;
  bannerGlowColor?: string;
  // Features props
  subtitle: string;
  features: ComingSoonFeature[];
  // Body
  children: ReactNode;
}

export default function ComingSoonModal({
  open,
  onClose,
  interest,
  bannerTitle,
  bannerTag1,
  bannerTag2,
  bannerGradient,
  bannerIconSrc,
  bannerGlowColor,
  subtitle,
  features,
  children,
}: ComingSoonModalProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [registered, setRegistered] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    // reset local state on each new open, not on close (avoids flash during close animation)
    setConfirmed(false);
    setRegistered(false);
    if (!user?.username) return;
    HTTPClient.get(`/marketing/user/${encodeURIComponent(user.username)}`)
      .then(({ data }) => {
        if (Array.isArray(data?.interests) && data.interests.includes(interest)) {
          setRegistered(true);
        }
      })
      .catch(() => {});
  }, [open, user?.username, interest]);

  const handleRegisterInterest = async () => {
    if (registered || loading || !user?.username) return;
    setLoading(true);
    try {
      await HTTPClient.put("/marketing/user", { email: user.username, interests: [interest] });
      setRegistered(true);
      setConfirmed(true);
    } catch {
      // best-effort — silent fail
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{
        "& > .MuiBox-root": {
          width: 396,
          maxWidth: "min(calc(100vw - 30px), 396px)",
          p: 0,
          py: 0,
          borderRadius: confirmed ? "19px" : "8px",
          border: confirmed ? "none" : "1.5px solid #E1E1E2",
          overflow: "hidden",
        },
      }}
    >
      {confirmed ? (
        // ── "C'est noté !" — écran de confirmation ───────────────────────────────
        <Box
          sx={{
            background: "linear-gradient(180deg, #0B33FF 0%, #4462FC 30.769%, #8FA1FF 100%)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: "30px",
            py: "40px",
            px: "20px",
            minHeight: 460,
            position: "relative",
            overflow: "hidden",
          }}
        >
          {/* Grain texture */}
          <Box sx={{ position: "absolute", inset: 0, zIndex: 0, backgroundImage: "url('/assets/recimg.png')", backgroundSize: "220px 220px", mixBlendMode: "soft-light", opacity: 0.7 }} />
          {/* Stars border */}
          <Box component="img" src="/assets/starsborder.png" alt="" sx={{ position: "absolute", left: -20, top: -10, width: 100, height: 100, zIndex: 0 }} />
          <Box component="img" src="/assets/starsborder.png" alt="" sx={{ position: "absolute", right: -20, top: -24, width: 157, zIndex: 0 }} />
          <Box component="img" src="/assets/starsborder.png" alt="" sx={{ position: "absolute", right: 200, top: 0, width: 40, height: 40, zIndex: 0 }} />

          <Box
            component="img"
            src="/assets/success.png"
            alt=""
            sx={{ width: 115, height: 134, objectFit: "contain", position: "relative", zIndex: 1 }}
          />
          <Typography
            fontFamily="Montserrat, sans-serif"
            fontWeight={700}
            fontSize={26}
            color="white"
            textAlign="center"
            letterSpacing="-0.26px"
            lineHeight="30px"
            sx={{ position: "relative", zIndex: 1 }}
          >
            {t("comingSoon.confirmed.title", "C'est noté ! 🎉")}
          </Typography>
          <Typography
            fontSize={16}
            color="white"
            textAlign="center"
            sx={{ opacity: 0.9, maxWidth: 250, letterSpacing: "-0.2px", lineHeight: "20px", position: "relative", zIndex: 1 }}
          >
            {t("comingSoon.confirmed.subtitle", "Vous serez parmi les premiers informés dès que cette fonctionnalité sera disponible.")}
          </Typography>
          <Box
            onClick={onClose}
            sx={{
              width: 310,
              height: 60,
              bgcolor: "#F59000",
              border: "3px solid #FFE2B8",
              borderRadius: "6px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              position: "relative",
              zIndex: 1,
            }}
          >
            <Typography fontSize={16} fontWeight={500} color="white">
              {t("common.close", "Fermer")}
            </Typography>
          </Box>
        </Box>
      ) : (
        // ── Vue normale / déjà inscrit ────────────────────────────────────────────
        <Box>
          <ComingSoonBanner title={bannerTitle} tag1={bannerTag1} tag2={bannerTag2} gradient={bannerGradient} iconSrc={bannerIconSrc} glowColor={bannerGlowColor} />

          {children}

          <ComingSoonFeatures subtitle={subtitle} features={features} />

          <Stack px={2} mt={2.5} mb={2} alignItems="center" gap={1}>
            {registered ? (
              <Box sx={{ display: "flex", width: "100%", mb: 0.5 }}>
                <Box sx={{ width: 10, flexShrink: 0, bgcolor: "#16a34a", borderRadius: "4px 0 0 4px" }} />
                <Box sx={{ flex: 1, bgcolor: "#dcfce7", px: "15px", py: "10px" }}>
                  <Typography fontSize={14} color="#166534" fontStyle="italic" lineHeight={1.4}>
                    {t("comingSoon.alreadyRegistered", "✅ Vous êtes déjà sur la liste ! Vous serez parmi les premiers informés dès que cette fonctionnalité sera disponible.")}
                  </Typography>
                </Box>
              </Box>
            ) : (
              <Button
                fullWidth
                variant="contained"
                disabled={loading}
                onClick={handleRegisterInterest}
                sx={{
                  bgcolor: "#3F5EFB",
                  borderRadius: "6px",
                  height: 50,
                  textTransform: "none",
                  fontSize: 16,
                  fontWeight: 500,
                  "&:hover": { bgcolor: "#3350e0" },
                  "&.Mui-disabled": { bgcolor: "#A0AFFD", color: "#fff" },
                }}
              >
                {t("comingSoon.cta", "Être informé en priorité")}
              </Button>
            )}
            <Typography onClick={onClose} fontSize={16} fontWeight={500} color="#202020" sx={{ cursor: "pointer", py: 0.5 }}>
              {t("common.close", "Fermer")}
            </Typography>
          </Stack>
        </Box>
      )}
    </Modal>
  );
}
